import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import type { TabStripMode, WorkspaceMode } from "@keydials/api-client";
import { api, getAccessToken } from "../auth/AuthContext";

interface WorkspaceModeState {
  /** What to render. Always "Classic" until the server has answered, and whenever the feature is off. */
  mode: WorkspaceMode;
  /** Whether the choice is offered at all. False ⇒ the avatar menu shows no toggle. */
  enabled: boolean;
  /** False while the first fetch is in flight, so nothing renders a toggle in the wrong position. */
  loaded: boolean;
  /** True while a change is being saved. */
  busy: boolean;
  /** Set when the server refused the last change, so the UI can say why the view snapped back rather than
   * leaving it as an unexplained flicker. Cleared on the next attempt. */
  refused: boolean;
  setMode: (mode: WorkspaceMode) => Promise<void>;
  /** Names or icons on the dial's tab strips. "Labels" until the server has said otherwise. */
  tabStrip: TabStripMode;
  /** Whether the server answered for the tab strip at all. False ⇒ offer no toggle: an API older than this
   * build can't keep the choice, and a switch that forgets itself on reload is worse than no switch. */
  tabStripAvailable: boolean;
  setTabStrip: (mode: TabStripMode) => Promise<void>;
  /** Whether Look back opens as a Then/Now compare mode (`Features:LookBackCompare`). False until the server
   * says otherwise, which keeps the Look back tab — the version that has always worked. */
  lookBackCompare: boolean;
  lookAhead: boolean;
}

const WorkspaceModeContext = createContext<WorkspaceModeState | undefined>(undefined);

/**
 * Which dial workspace this person is shown (KEY-531).
 *
 * The value is held by the SERVER, not localStorage, and that is deliberate — see
 * `UserWorkspacePreference`. This product runs on shared tablets, where a browser key is inherited by the
 * next pair of hands; `InstanceViewPreference` was moved off localStorage for exactly that reason, and so,
 * since KEY-755, was the theme.
 *
 * "Classic" is the safe answer to every uncertainty: before the first fetch returns, when the feature is off,
 * and when the call fails. A workspace that flickers into the redesign and back out is worse than one that
 * arrives a beat late.
 */
export function WorkspaceModeProvider({ children }: { children: ReactNode }) {
  const [mode, setModeState] = useState<WorkspaceMode>("Classic");
  const [enabled, setEnabled] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [refused, setRefused] = useState(false);
  const [tabStrip, setTabStripState] = useState<TabStripMode>("Labels");
  const [tabStripAvailable, setTabStripAvailable] = useState(false);
  const [lookBackCompare, setLookBackCompare] = useState(false);
  const [lookAhead, setLookAhead] = useState(false);

  // Its own call, and not behind the dynamic-workspace flag: a crowded strip is crowded in Classic too.
  useEffect(() => {
    const token = getAccessToken();
    if (!token) return;
    let cancelled = false;
    api.tabStripMode(token)
      .then((view) => {
        if (cancelled) return;
        setTabStripState(view.mode);
        setTabStripAvailable(true);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const setTabStrip = useCallback(async (next: TabStripMode) => {
    const token = getAccessToken();
    if (!token) return;
    const prior = tabStrip;
    setTabStripState(next); // optimistic, like the workspace mode: the strip redraws at once
    try {
      setTabStripState((await api.setTabStripMode(token, next)).mode);
    } catch {
      setTabStripState(prior);
    }
  }, [tabStrip]);

  useEffect(() => {
    const token = getAccessToken();
    if (!token) {
      setLoaded(true);
      return;
    }
    let cancelled = false;
    api.workspaceMode(token)
      .then((view) => {
        if (cancelled) return;
        setEnabled(view.enabled);
        setModeState(view.enabled ? view.mode : "Classic");
        setLookBackCompare(!!view.lookBackCompare);
        setLookAhead(!!view.lookAhead);
      })
      // A failure leaves Classic in place: the workspace that has always worked is the right thing to fall
      // back to, and the toggle stays hidden rather than offering a switch that cannot be saved.
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const setMode = useCallback(async (next: WorkspaceMode) => {
    const token = getAccessToken();
    if (!token) return;
    const prior = mode;
    setBusy(true);
    setRefused(false);
    setModeState(next); // optimistic: the whole workspace re-renders, so waiting reads as a hang
    try {
      const view = await api.setWorkspaceMode(token, next);
      setEnabled(view.enabled);
      setModeState(view.enabled ? view.mode : "Classic");
    } catch {
      // Reverting silently is what makes a refusal read as a flicker — the view appears to try and then
      // change its mind for no reason. The likeliest cause is a server that predates the view being asked
      // for: this app deploys separately from the API, so a browser can be a build ahead of it.
      setModeState(prior);
      setRefused(true);
    } finally {
      setBusy(false);
    }
  }, [mode]);

  const value = useMemo<WorkspaceModeState>(
    () => ({ mode, enabled, loaded, busy, refused, setMode, tabStrip, tabStripAvailable, setTabStrip, lookBackCompare, lookAhead }),
    [mode, enabled, loaded, busy, refused, setMode, tabStrip, tabStripAvailable, setTabStrip, lookBackCompare, lookAhead],
  );

  return <WorkspaceModeContext.Provider value={value}>{children}</WorkspaceModeContext.Provider>;
}

export function useWorkspaceMode(): WorkspaceModeState {
  const value = useContext(WorkspaceModeContext);
  // Deliberately not a throw: every dial surface asks this, and a missing provider should degrade to the
  // workspace that has always worked rather than blanking the page.
  return value ?? {
    mode: "Classic", enabled: false, loaded: true, busy: false, refused: false, setMode: async () => {},
    tabStrip: "Labels", tabStripAvailable: false, setTabStrip: async () => {}, lookBackCompare: false, lookAhead: false,
  };
}
